import React, { useCallback, useEffect, useState } from "react";
import { Mail, CheckCircle2, AlertTriangle, Copy, RefreshCw, Send, Eye, Inbox } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Chip, Button, Skeleton, Toast, Input } from "@/ui";

function Copyable({ label, value }) {
  const [done, setDone] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(value || ""); setDone(true); setTimeout(() => setDone(false), 1500); };
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg px-3 py-2" style={{ background: "var(--sunk)" }}>
      <div className="min-w-0"><div className="text-xs font-semibold">{label}</div><div className="text-xs num truncate" style={{ color: "var(--text-2)" }}>{value}</div></div>
      <Button data-testid={`copy-${label}`} onClick={copy}><Copy size={13} />{done ? "copied" : "copy"}</Button>
    </div>
  );
}

export default function EmailSetup() {
  const [status, setStatus] = useState(null);
  const [domain, setDomain] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [dead, setDead] = useState([]);
  const [key, setKey] = useState("");
  const [dom, setDom] = useState("");
  const [from, setFrom] = useState("");
  const [preview, setPreview] = useState(null);
  const [toast, setToast] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, d, t, dl] = await Promise.all([
        api.get("/email/status"), api.get("/email/domain"),
        api.get("/email/templates"), api.get("/email/dead-letters"),
      ]);
      setStatus(s.data); setDomain(d.data); setTemplates(t.data.templates); setDead(dl.data.deadLetters);
      setDom(s.data.sendingDomain || ""); setFrom(s.data.from || "");
    } catch (e) { setToast(friendlyError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 3500); };
  const saveKey = async () => {
    setBusy("save");
    try { await api.post("/email/config", { apiKey: key || undefined, sendingDomain: dom, emailFrom: from }); setKey(""); flash("Saved. Now press Test to send yourself a real email."); load(); }
    catch (e) { flash(friendlyError(e)); } finally { setBusy(""); }
  };
  const test = async () => { setBusy("test"); try { const r = await api.post("/email/test", {}); flash(r.data.message); } catch (e) { flash(friendlyError(e)); } finally { setBusy(""); } };
  const recheck = async () => { setBusy("dns"); try { const r = await api.get("/email/domain"); setDomain(r.data); flash(r.data.message); } catch (e) { flash(friendlyError(e)); } finally { setBusy(""); } };
  const showPreview = async (tpl) => { try { const r = await api.get(`/email/preview?template=${tpl}`); setPreview(r.data); } catch (e) { flash(friendlyError(e)); } };
  const sendTpl = async (tpl) => { try { const r = await api.post("/email/templates/test", { template: tpl }); flash(r.data.message); } catch (e) { flash(friendlyError(e)); } };
  const retry = async (id) => { try { await api.post(`/email/dead-letters/${id}/retry`); flash("Retried."); load(); } catch (e) { flash(friendlyError(e)); } };

  return (
    <Layout>
      <div className="flex items-center gap-2 mb-1"><Mail size={22} strokeWidth={1.75} /><h1 className="t-h1">Email</h1></div>
      <p className="muted t-small mb-5">Set up transactional email so receipts, alerts and sign-in codes go out on your own domain.</p>

      {!status ? <Skeleton style={{ height: 140 }} /> : (
        <>
          {/* 3. Live status line + what works without it */}
          <Card className="p-4 mb-4" data-testid="email-status">
            <div className="flex items-center gap-2">
              {status.connected ? <Chip tone="good" data-testid="email-connected"><CheckCircle2 size={13} className="inline" /> Connected</Chip>
                : <Chip tone="bad" data-testid="email-connected"><AlertTriangle size={13} className="inline" /> Not connected yet</Chip>}
              {status.source !== "none" ? <span className="text-xs" style={{ color: "var(--text-3)" }}>key from {status.source}</span> : null}
            </div>
            <div className="grid grid-3 mt-3" style={{ gap: 10 }}>
              <div><div className="text-xs" style={{ color: "var(--text-3)" }}>Last successful send</div><div className="text-sm num">{status.lastSuccessAt ? new Date(status.lastSuccessAt).toLocaleString() : "—"}</div></div>
              <div><div className="text-xs" style={{ color: "var(--text-3)" }}>Sent this week</div><div className="text-sm num">{status.sentThisWeek}</div></div>
              <div><div className="text-xs" style={{ color: "var(--text-3)" }}>Bounced / stuck</div><div className="text-sm num">{status.bouncedThisWeek}</div></div>
            </div>
            {!status.connected ? (
              <div className="grid grid-2 mt-3" style={{ gap: 10 }}>
                <div className="rounded-xl p-3" style={{ background: "#f0fdf4" }} data-testid="works-without">
                  <div className="text-xs font-semibold" style={{ color: "var(--good)" }}>Still works without email</div>
                  <ul className="text-xs mt-1" style={{ color: "var(--good)", paddingLeft: 16 }}>{status.worksWithout.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
                <div className="rounded-xl p-3" style={{ background: "#fef2f2" }} data-testid="broken-without">
                  <div className="text-xs font-semibold" style={{ color: "var(--bad)" }}>Won't work until connected</div>
                  <ul className="text-xs mt-1" style={{ color: "var(--bad)", paddingLeft: 16 }}>{status.brokenWithout.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
              </div>
            ) : null}
          </Card>

          {/* 1. Key field + Test */}
          <Card className="p-4 mb-4" data-testid="email-key-card">
            <div className="text-sm font-semibold mb-2">Your Emailit key</div>
            <div className="flex gap-2 flex-wrap items-center">
              <Input type="password" placeholder="Paste your Emailit API key" value={key} data-testid="email-key-input" onChange={(e) => setKey(e.target.value)} style={{ minWidth: 240 }} />
              <Button data-testid="email-key-save" loading={busy === "save"} onClick={saveKey}>save</Button>
              <Button variant="primary" data-testid="email-test" loading={busy === "test"} onClick={test}><Send size={14} />Test</Button>
            </div>
            <div className="flex gap-2 flex-wrap mt-2">
              <Input placeholder="Sending domain (e.g. mail.yourshop.ph)" value={dom} data-testid="email-domain-input" onChange={(e) => setDom(e.target.value)} style={{ minWidth: 240 }} />
              <Input placeholder="From (e.g. Shop <hello@mail.yourshop.ph>)" value={from} data-testid="email-from-input" onChange={(e) => setFrom(e.target.value)} style={{ minWidth: 240 }} />
            </div>
            <p className="text-xs mt-2" style={{ color: "var(--text-3)" }}>Test sends a real email to you and tells you exactly what happened.</p>
          </Card>

          {/* 2. Domain checklist */}
          <Card className="p-4 mb-4" data-testid="email-domain-card">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Domain setup {domain?.verified ? <Chip tone="good">verified</Chip> : <Chip tone="muted">not verified</Chip>}</div>
              <Button data-testid="email-dns-recheck" loading={busy === "dns"} onClick={recheck}><RefreshCw size={14} />Check again</Button>
            </div>
            <p className="text-xs mt-1 mb-2" style={{ color: "var(--text-3)" }}>{domain?.message}</p>
            <div className="stack" style={{ gap: 8 }}>
              {(domain?.checklist || []).map((c, i) => (
                <div key={i} data-testid={`dns-row-${c.name}`}>
                  <div className="flex items-center gap-2 text-xs mb-1">
                    {c.ok ? <CheckCircle2 size={13} color="var(--good)" /> : <AlertTriangle size={13} color="var(--warn)" />}
                    <span className="font-semibold">{c.name}</span><Chip tone="muted">{c.type}</Chip>
                  </div>
                  <Copyable label={c.name} value={c.value} />
                </div>
              ))}
            </div>
          </Card>

          {/* 4. Template previews */}
          <Card className="p-4 mb-4" data-testid="email-templates-card">
            <div className="text-sm font-semibold mb-2">Every email, previewed with your shop's data</div>
            <div className="stack" style={{ gap: 6 }}>
              {templates.map((t) => (
                <div key={t.key} className="flex items-center justify-between gap-2 rounded-lg px-3 py-2" style={{ background: "var(--sunk)" }} data-testid={`tpl-row-${t.key}`}>
                  <span className="text-sm">{t.label}</span>
                  <div className="flex gap-2">
                    <Button data-testid={`tpl-preview-${t.key}`} onClick={() => showPreview(t.key)}><Eye size={13} />preview</Button>
                    <Button data-testid={`tpl-send-${t.key}`} onClick={() => sendTpl(t.key)}><Send size={13} />Send me a test</Button>
                  </div>
                </div>
              ))}
            </div>
            {preview ? (
              <div className="mt-3 rounded-xl border p-3" data-testid="email-preview" style={{ borderColor: "var(--border)" }}>
                <div className="text-xs" style={{ color: "var(--text-3)" }}>Subject</div>
                <div className="text-sm font-semibold mb-2">{preview.subject}</div>
                <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden", background: "var(--surface)" }} dangerouslySetInnerHTML={{ __html: preview.html }} />
              </div>
            ) : null}
          </Card>

          {/* 5. Dead-letter list */}
          <Card className="p-4" data-testid="email-dead-card">
            <div className="flex items-center gap-2 text-sm font-semibold mb-2"><Inbox size={16} strokeWidth={1.75} />Couldn't be delivered {dead.length ? <Chip tone="bad">{dead.length}</Chip> : <Chip tone="good">0</Chip>}</div>
            {dead.length ? dead.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 mb-1" style={{ background: "#fef2f2" }} data-testid={`dead-${d.id}`}>
                <div className="min-w-0"><div className="text-sm font-semibold">{d.template} → {d.to}</div><div className="text-xs truncate" style={{ color: "var(--bad)" }}>{d.lastError} · {d.attempts} tries</div></div>
                <Button data-testid={`dead-retry-${d.id}`} onClick={() => retry(d.id)}><RefreshCw size={13} />retry</Button>
              </div>
            )) : <div className="text-sm" style={{ color: "var(--text-3)" }}>Nothing stuck — every email went out.</div>}
          </Card>
        </>
      )}

      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="email-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}
